import React, { useState, useEffect } from "react";
import { Modal, Table, Spin, message, Tabs, Select, Pagination } from "antd";
import { Pie } from "@ant-design/plots";
import { readItems, readUsers } from "@directus/sdk";
import { getDirectusClient } from "../../../lib/directus";

const RespuestasModal = ({ visible, onCancel, encuestaId }) => {
  const [loading, setLoading] = useState(false);
  const [respuestas, setRespuestas] = useState([]);
  const [preguntas, setPreguntas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [opcionesMultiples, setOpcionesMultiples] = useState([]);
  const [opcionesUnicas, setOpcionesUnicas] = useState([]);
  const [activeTab, setActiveTab] = useState("summary");
  const [selectedUser, setSelectedUser] = useState(null);
  const [responsePagination, setResponsePagination] = useState({});

  const getPieChartData = (respuestas, tipo, preguntaId) => {
    const respuestasPregunta = respuestas.filter(
      (r) => r.pregunta.id === preguntaId
    );

    if (tipo === "unica") {
      const frecuencias = {};
      respuestasPregunta.forEach((resp) => {
        const opcion = opcionesUnicas.find((o) => o.id === resp.unica);
        if (opcion) {
          frecuencias[opcion.respuesta] =
            (frecuencias[opcion.respuesta] || 0) + 1;
        }
      });
      return Object.entries(frecuencias).map(([name, value]) => ({
        name,
        value,
      }));
    } else if (tipo === "multiple") {
      const frecuencias = {};
      respuestasPregunta.forEach((resp) => {
        (resp.multiples || []).forEach((relacion) => {
          const opcion = opcionesMultiples.find(
            (o) => o.id === relacion.opciones_multiple_id.id
          );
          if (opcion) {
            frecuencias[opcion.respuesta] =
              (frecuencias[opcion.respuesta] || 0) + 1;
          }
        });
      });
      return Object.entries(frecuencias).map(([name, value]) => ({
        name,
        value,
      }));
    }
    return [];
  };

  const getPieConfig = (data) => ({
    data,
    angleField: "value",
    colorField: "name",
    radius: 0.8,
    legend: {
      position: "bottom",
    },
    label: {
      text: "name",
    },
    interactions: [
      {
        type: "element-highlighted",
      },
    ],
    height: 300,
  });

  const groupResponses = (respuestas, preguntas) => {
    return preguntas.map((pregunta) => {
      const respuestasPregunta = respuestas.filter(
        (r) => r.pregunta.id === pregunta.id
      );

      return {
        key: pregunta.id,
        pregunta: pregunta.pregunta,
        tipo: pregunta.tipo_respuesta,
        respuestas: respuestasPregunta.map((resp) => ({
          id: resp.id,
          valor: formatRespuesta(resp, pregunta.tipo_respuesta),
          cliente: usuarios.find((u) => u.id === resp.cliente),
        })),
        chartData: ["unica", "multiple"].includes(pregunta.tipo_respuesta)
          ? getPieChartData(respuestas, pregunta.tipo_respuesta, pregunta.id)
          : null,
      };
    });
  };

  const formatRespuesta = (respuesta, tipo) => {
    switch (tipo) {
      case "texto":
        return respuesta.texto || "-";
      case "numero":
        return respuesta.numero || "-";
      case "unica":
        const opcionUnica = opcionesUnicas.find(
          (o) => o.id === respuesta.unica
        );
        return opcionUnica?.respuesta || "-";
      case "multiple":
        if (!respuesta.multiples?.length) return "-";
        return respuesta.multiples
          .map((relacion) => 
            opcionesMultiples.find((o) => o.id === relacion.opciones_multiple_id.id)?.respuesta
          )
          .filter(Boolean)
          .join(", ");
      default:
        return "-";
    }
  };

  const columns = [
    {
      title: "Pregunta",
      dataIndex: "pregunta",
      key: "pregunta",
      render: (text, record) => (
        <div>
          <div>{text}</div>
          <small style={{ color: "#666" }}>Tipo: {record.tipo}</small>
        </div>
      ),
    },
    {
      title: "Respuestas",
      dataIndex: "respuestas",
      key: "respuestas",
      render: (respuestas, record) => (
        <div>
          {record.chartData && record.chartData.length > 0 ? (
            <div style={{ marginBottom: "16px" }}>
              <Pie {...getPieConfig(record.chartData)} />
            </div>
          ) : (
            <>
              <ul style={{ listStyle: "none", padding: 0 }}>
                {respuestas
                  .slice(
                    (responsePagination[record.key]?.current || 1) * 5 - 5,
                    (responsePagination[record.key]?.current || 1) * 5
                  )
                  .map((resp, index) => (
                    <li
                      key={resp.id}
                      style={{
                        marginBottom: "8px",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <div>{resp.valor}</div>
                      <small style={{ color: "#666" }}>
                        Por:{" "}
                        {resp.cliente
                          ? `${resp.cliente.first_name} ${resp.cliente.last_name}`
                          : "-"}
                      </small>
                    </li>
                  ))}
              </ul>
              {respuestas.length > 5 && (
                <div style={{ textAlign: 'right', marginTop: 8 }}>
                  <Pagination
                    size="small"
                    total={respuestas.length}
                    pageSize={5}
                    current={responsePagination[record.key]?.current || 1}
                    onChange={(page) => {
                      setResponsePagination(prev => ({
                        ...prev,
                        [record.key]: { current: page }
                      }));
                    }}
                    showSizeChanger={false}
                  />
                </div>
              )}
            </>
          )}
        </div>
      ),
    },
  ];

  const individualColumns = [
    {
      title: "Pregunta",
      dataIndex: "pregunta",
      key: "pregunta",
    },
    {
      title: "Tipo",
      dataIndex: "tipo",
      key: "tipo",
    },
    {
      title: "Respuesta",
      dataIndex: "respuesta",
      key: "respuesta",
    }
  ];

  const getIndividualData = () => {
    if (!selectedUser) return [];
    
    return preguntas
      .map(pregunta => {
        const respuesta = respuestas.find(
          r => r.pregunta.id === pregunta.id && r.cliente === selectedUser
        );

        if (!respuesta) return null;

        return {
          key: pregunta.id,
          pregunta: pregunta.pregunta,
          tipo: pregunta.tipo_respuesta,
          respuesta: formatRespuesta(respuesta, pregunta.tipo_respuesta)
        };
      })
      .filter(Boolean);
  };

  const getUniqueUsers = () => {
    const uniqueUsers = [...new Set(respuestas.map(r => r.cliente))];
    return usuarios
      .filter(user => uniqueUsers.includes(user.id))
      .map(user => ({
        label: `${user.first_name} ${user.last_name}`,
        value: user.id
      }));
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const directus = await getDirectusClient();

        const [preguntasResponse, respuestasResponse] = await Promise.all([
          directus.request(
            readItems("preguntas", {
              fields: [
                "id",
                "pregunta",
                "tipo_respuesta"
              ],
              filter: {
                encuesta: {
                  id: {
                    _eq: encuestaId
                  }
                }
              }
            })
          ),
          directus.request(
            readItems("respuestas", {
              fields: [
                "id",
                "texto",
                "numero",
                "unica",
                "multiples.opciones_multiple_id.id",
                "pregunta.id",
                "cliente"
              ],
              filter: {
                pregunta: {
                  encuesta: {
                    _eq: encuestaId
                  }
                }
              }
            })
          )
        ]);

        setPreguntas(preguntasResponse);


        
        

        // Get all IDs for subsequent requests
        const clienteIds = [...new Set(respuestasResponse.map(r => r.cliente))].filter(Boolean);
        const multipleIds = [...new Set(respuestasResponse.flatMap(r => r.multiples || []))];
        const unicaIds = [...new Set(respuestasResponse.map(r => r.unica).filter(Boolean))];


        

        const multipleIds2 = respuestasResponse
          .flatMap(r => r.multiples || [])
          .map(m => m.opciones_multiple_id.id);
        
        // Execute all secondary requests in parallel
        const [usuarios, opcionesMultiples, opcionesUnicas] = await Promise.all([
          clienteIds.length > 0 ? directus.request(
            readUsers({
              fields: ["id", "first_name", "last_name"],
              filter: { id: { _in: clienteIds } }
            })
          ) : [],
          multipleIds2.length > 0 ? directus.request(
            readItems("opciones_multiple", {
              fields: ["id", "respuesta"],
              filter: { id: { _in: multipleIds2 } }
            })
          ) : [],
          unicaIds.length > 0 ? directus.request(
            readItems("opcion_multiple_una", {
              fields: ["id", "respuesta"],
              filter: { id: { _in: unicaIds } }
            })
          ) : []
        ]);

        setRespuestas(respuestasResponse);
        setUsuarios(usuarios);
        setOpcionesMultiples(opcionesMultiples);
        setOpcionesUnicas(opcionesUnicas);

      } catch (error) {
        console.error("Error fetching data:", error);
        message.error("Error al cargar los datos");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [encuestaId]);

  return (
    <Modal
      title="Respuestas de la Encuesta"
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={null}
    >
      {loading ? (
        <Spin />
      ) : (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "summary",
              label: "Resumen",
              children: (
                <Table
                  dataSource={groupResponses(respuestas, preguntas)}
                  columns={columns}
                  rowKey="key"
                  pagination={{ pageSize: 5 }}
                />
              ),
            },
            {
              key: "individual",
              label: "Individual",
              children: (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Select
                      placeholder="Seleccionar usuario"
                      style={{ width: '100%' }}
                      options={getUniqueUsers()}
                      onChange={setSelectedUser}
                      value={selectedUser}
                    />
                  </div>
                  <Table
                    dataSource={getIndividualData()}
                    columns={individualColumns}
                    rowKey="key"
                    pagination={{ pageSize: 10 }}
                  />
                </div>
              ),
            },
          ]}
        />
      )}
    </Modal>
  );
};

export default RespuestasModal;
